import { useRef, useState, useEffect } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { ANIMATION_SPEED } from '../../constants/config';
import type { ResponsiveValues } from '../../types';
import { useAppStore } from '../../store/useAppStore';

interface SpiralAnimationReturn {
  scrollRef: React.RefObject<number>;
  initAnimation: number;
  setInitAnimation: React.Dispatch<React.SetStateAction<number>>;
  isInitialAnimationComplete: React.MutableRefObject<boolean>;
  hasScrolled: boolean;
  velocity: React.RefObject<number>;
}

export const useSpiralAnimation = (
  _groupRef: React.RefObject<THREE.Group>,
  _values: ResponsiveValues,
  onScrollInteraction?: (
    clientX: number,
    clientY: number,
    isTouch?: boolean
  ) => void,
  onTouchStart?: () => void
): SpiralAnimationReturn => {
  const VELOCITY_DAMPING_PER_FRAME = 0.975;
  const MAX_VELOCITY = 0.01;
  const MIN_SCROLL_VALUE = 0.03;
  const LERP_SPEED = 0.1;
  const hasVisitedAbout = useAppStore(state => state.hasVisitedAbout);
  const scrollRef = useRef<number>(
    hasVisitedAbout ? useAppStore.getState().scrollOffset : 0
  );
  const [initAnimation, setInitAnimation] = useState<number>(() =>
    hasVisitedAbout ? 1 : 0
  );
  const isInitialAnimationComplete = useRef<boolean>(hasVisitedAbout);

  // Sync immédiat au retour d'About (évite tout timing issue)
  useEffect(() => {
    if (hasVisitedAbout) {
      setInitAnimation(1);
      isInitialAnimationComplete.current = true;
      useAppStore.getState().setInitialAnimationComplete(true);
    }
  }, [hasVisitedAbout]);
  const velocity = useRef<number>(0);
  const isScrolling = useRef<boolean>(false);
  const selectedProject = useAppStore(state => state.selectedProject);
  const setScrollOffset = useAppStore(state => state.setScrollOffset);
  const setVelocity = useAppStore(state => state.setVelocity);
  const [hasScrolled] = useState(false);

  useEffect(() => {
    const applyDelta = (
      deltaY: number,
      clientX: number,
      clientY: number,
      isTouch = false
    ) => {
      onScrollInteraction?.(clientX, clientY, isTouch);
      useAppStore.getState().setHasScrolled(true);
      const delta = deltaY * ANIMATION_SPEED.SCROLL;
      const currentVelocity = velocity.current;
      const distanceToLimit = Math.abs(currentVelocity) / MAX_VELOCITY;
      const dampingFactor = Math.max(0, 1 - distanceToLimit);
      const newVelocity = currentVelocity + delta * dampingFactor;
      velocity.current = Math.min(
        Math.max(newVelocity, -MAX_VELOCITY),
        MAX_VELOCITY
      );
      setVelocity(Math.abs(velocity.current));
      isScrolling.current = true;
    };

    const handleWheel = (event: WheelEvent) => {
      if (selectedProject || !isInitialAnimationComplete.current) return;
      event.preventDefault();
      applyDelta(event.deltaY, event.clientX, event.clientY, false);
    };

    let lastTouchY = 0;
    const TOUCH_SCROLL_FACTOR = 4;
    let isMouseDragging = false;
    let lastMouseY = 0;
    let smoothedMouseDelta = 0;
    const MOUSE_DRAG_SCROLL_FACTOR = 2.8;
    const MOUSE_DRAG_SMOOTHING = 0.22;

    const handleTouchStart = (e: TouchEvent) => {
      if (selectedProject || !isInitialAnimationComplete.current) return;
      lastTouchY = e.touches[0].clientY;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (selectedProject || !isInitialAnimationComplete.current) return;
      e.preventDefault();
      const touch = e.touches[0];
      const currentY = touch.clientY;
      const deltaY = lastTouchY - currentY;
      lastTouchY = currentY;
      applyDelta(
        deltaY * TOUCH_SCROLL_FACTOR,
        touch.clientX,
        touch.clientY,
        true
      );
    };

    const isInteractiveTarget = (target: EventTarget | null) => {
      if (!(target instanceof Element)) return false;
      return Boolean(
        target.closest(
          'button, a, input, select, textarea, [data-no-gallery-drag]'
        )
      );
    };

    const setDraggingCursor = (isDragging: boolean) => {
      if (typeof document === 'undefined') return;
      document.body.style.cursor = isDragging ? 'grabbing' : '';
      document.body.style.userSelect = isDragging ? 'none' : '';
    };

    const handleMouseDown = (e: MouseEvent) => {
      if (selectedProject || !isInitialAnimationComplete.current) return;
      if (e.button !== 0) return;
      if (isInteractiveTarget(e.target)) return;
      isMouseDragging = true;
      lastMouseY = e.clientY;
      smoothedMouseDelta = 0;
      setDraggingCursor(true);
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isMouseDragging) return;
      if (selectedProject || !isInitialAnimationComplete.current) return;
      e.preventDefault();
      const rawDeltaY = lastMouseY - e.clientY;
      lastMouseY = e.clientY;
      smoothedMouseDelta += (rawDeltaY - smoothedMouseDelta) * MOUSE_DRAG_SMOOTHING;
      applyDelta(
        smoothedMouseDelta * MOUSE_DRAG_SCROLL_FACTOR,
        e.clientX,
        e.clientY,
        false
      );
    };

    const stopMouseDrag = () => {
      if (!isMouseDragging) return;
      isMouseDragging = false;
      smoothedMouseDelta = 0;
      setDraggingCursor(false);
    };

    window.addEventListener('wheel', handleWheel, { passive: false });
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', stopMouseDrag);
    window.addEventListener('mouseleave', stopMouseDrag);

    return () => {
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', stopMouseDrag);
      window.removeEventListener('mouseleave', stopMouseDrag);
      setDraggingCursor(false);
    };
  }, [selectedProject, setVelocity, setScrollOffset, onScrollInteraction, onTouchStart]);

  useFrame((_, delta) => {
    const { scrollOffset, isIndicatorScrolling } = useAppStore.getState();
    const targetOffset = scrollOffset ?? 0;

    if (isIndicatorScrolling) {
      scrollRef.current = targetOffset;
      return;
    }

    // Toujours traiter le scroll manuel (wheel/touch) même après retour d'About
    if (isScrolling.current) {
      const frameScale = Math.min(delta * 120, 2);
      const motionScale = Math.sqrt(frameScale);
      scrollRef.current =
        (scrollRef.current + velocity.current * motionScale + Math.PI * 2) %
        (Math.PI * 2);
      setScrollOffset(scrollRef.current);

      velocity.current *= Math.pow(VELOCITY_DAMPING_PER_FRAME, motionScale);
      const absVel = Math.abs(velocity.current);
      if (absVel < 0.00001) {
        isScrolling.current = false;
        velocity.current = 0;
        setVelocity(0);
      } else {
        setVelocity(absVel);
      }
      return;
    }

    if (hasVisitedAbout) {
      scrollRef.current = targetOffset;
      return;
    }

    const diff = targetOffset - scrollRef.current;
    if (Math.abs(diff) > MIN_SCROLL_VALUE) {
      const step = diff * LERP_SPEED;
      const limitedStep = Math.min(
        Math.max(step, -MAX_VELOCITY),
        MAX_VELOCITY
      );
      scrollRef.current =
        (scrollRef.current + limitedStep + Math.PI * 2) % (Math.PI * 2);
      setScrollOffset(scrollRef.current);
    } else {
      scrollRef.current = targetOffset;
    }
  });

  return {
    scrollRef,
    initAnimation,
    setInitAnimation,
    isInitialAnimationComplete,
    hasScrolled,
    velocity,
  };
};
